import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  searchAthletesByName,
  markAttendance,
  getAthleteAttendanceStatus,
  AthleteSearchResult,
} from "../../api/kioskAPI";
import "./KioskMakeupModal.css";
import { captureKioskError } from "../../utils/sentry";
import { useParticipantLabel } from "../../hooks/useParticipantLabel";

interface KioskMakeupModalProps {
  slotId: string;
  programLabel: string;
  onClose: () => void;
  onSuccess: (athleteName: string) => void;
}

const MIN_SEARCH_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

const KioskMakeupModal: React.FC<KioskMakeupModalProps> = ({
  slotId,
  programLabel,
  onClose,
  onSuccess,
}) => {
  const participant = useParticipantLabel();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<AthleteSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAthlete, setSelectedAthlete] = useState<AthleteSearchResult | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [alreadyPresent, setAlreadyPresent] = useState(false);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length < MIN_SEARCH_CHARS) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchAthletesByName(searchQuery);
        if (response.success && response.data) {
          setSearchResults(response.data);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        captureKioskError(err, "searchAthletes", { slotId });
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // slotId is read inside the catch, to tag the error report. Left out of
    // the deps it goes stale, so a failed search in one slot could be
    // reported against whichever slot the modal opened on.
  }, [searchQuery, slotId]);

  // Check if athlete is already present when selected
  const handleSelectAthlete = useCallback(
    async (athlete: AthleteSearchResult) => {
      setSelectedAthlete(athlete);
      setCheckingStatus(true);
      setAlreadyPresent(false);
      setError(null);

      try {
        const response = await getAthleteAttendanceStatus(athlete.id);
        if (response.success && response.data) {
          // Check if already present for this slot
          const slotStatus = response.data.attendance?.find((s) => s.slotId === slotId);
          if (slotStatus?.alreadyMarked) {
            setAlreadyPresent(true);
          }
        }
      } catch (err) {
        captureKioskError(err, "checkAttendanceStatus", { slotId });
      } finally {
        setCheckingStatus(false);
      }
    },
    [slotId]
  );

  const handleMarkPresent = useCallback(async () => {
    if (!selectedAthlete || alreadyPresent) return;

    setLoading(true);
    setError(null);

    try {
      const response = await markAttendance(selectedAthlete.id, slotId, true);
      if (response.success) {
        onSuccess(`${selectedAthlete.firstName} ${selectedAthlete.lastName}`);
      } else {
        setError(response.error || "Failed to mark attendance");
      }
    } catch (err) {
      setError("Unable to mark attendance. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedAthlete, slotId, alreadyPresent, onSuccess]);

  const handleBack = useCallback(() => {
    if (selectedAthlete) {
      setSelectedAthlete(null);
      setAlreadyPresent(false);
      setError(null);
    } else {
      onClose();
    }
  }, [selectedAthlete, onClose]);

  return (
    <div className="kiosk-makeup-modal__overlay" onClick={onClose}>
      <div
        className="kiosk-makeup-modal__content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="kiosk-makeup-modal__header">
          <button className="kiosk-makeup-modal__back" onClick={handleBack}>
            <span className="kiosk-makeup-modal__back-icon">&#8249;</span>
            {selectedAthlete ? "Back" : "Cancel"}
          </button>
          <h2 className="kiosk-makeup-modal__title">
            {selectedAthlete ? "Confirm Check-In" : "Make Up Attendance"}
          </h2>
          <div className="kiosk-makeup-modal__spacer" />
        </div>

        {!selectedAthlete ? (
          /* Search View */
          <>
            <div className="kiosk-makeup-modal__search">
              <p className="kiosk-makeup-modal__subtitle">
                Search for {participant.singularLower} by name to mark makeup attendance for{" "}
                <strong>{programLabel}</strong>
              </p>
              <div className="kiosk-makeup-modal__input-wrapper">
                <input
                  aria-label={`${participant.singular} name`}
                  ref={inputRef}
                  type="text"
                  className="kiosk-makeup-modal__input"
                  placeholder={`Enter ${participant.singularLower} name (min 3 characters)`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searching && (
                  <div className="kiosk-makeup-modal__input-spinner" />
                )}
              </div>
            </div>

            <div className="kiosk-makeup-modal__results">
              {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_CHARS && (
                <p className="kiosk-makeup-modal__hint">
                  Type at least {MIN_SEARCH_CHARS} characters to search
                </p>
              )}

              {searchQuery.length >= MIN_SEARCH_CHARS && !searching && searchResults.length === 0 && (
                <div className="kiosk-makeup-modal__empty">
                  <span className="kiosk-makeup-modal__empty-icon">&#128269;</span>
                  <p>No {participant.pluralLower} found matching "{searchQuery}"</p>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="kiosk-makeup-modal__athlete-list">
                  {searchResults.map((athlete) => (
                    <button
                      key={athlete.id}
                      className="kiosk-makeup-modal__athlete-card"
                      onClick={() => handleSelectAthlete(athlete)}
                    >
                      <span className="kiosk-makeup-modal__athlete-name">
                        {athlete.firstName} {athlete.lastName}
                      </span>
                      <span className="kiosk-makeup-modal__athlete-arrow">
                        &#8250;
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Confirmation View */
          <div className="kiosk-makeup-modal__confirm">
            <div className="kiosk-makeup-modal__confirm-card">
              <div className="kiosk-makeup-modal__confirm-avatar">
                {selectedAthlete.firstName.charAt(0)}
                {selectedAthlete.lastName.charAt(0)}
              </div>
              <h3 className="kiosk-makeup-modal__confirm-name">
                {selectedAthlete.firstName} {selectedAthlete.lastName}
              </h3>
              <p className="kiosk-makeup-modal__confirm-program">
                {programLabel}
              </p>
              <span className="kiosk-makeup-modal__confirm-badge">
                Make Up
              </span>
            </div>

            {checkingStatus ? (
              <div className="kiosk-makeup-modal__checking">
                <div className="kiosk-makeup-modal__checking-spinner" />
                <p>Checking status...</p>
              </div>
            ) : alreadyPresent ? (
              <div className="kiosk-makeup-modal__already-present">
                <span className="kiosk-makeup-modal__already-icon">&#10003;</span>
                <p>Already checked in for this class today</p>
              </div>
            ) : error ? (
              <div className="kiosk-makeup-modal__error">
                <p>{error}</p>
              </div>
            ) : (
              <button
                className="kiosk-makeup-modal__confirm-btn"
                onClick={handleMarkPresent}
                disabled={loading}
              >
                {loading ? "Marking Present..." : "Confirm Check-In"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KioskMakeupModal;
